/* @jsx h */
import { defineComponent, ref, h, onMounted, computed } from "vue";

import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { ReservationTag } from "@lemmonhq/types";

const TAG_COLORS = [
  { name: "Red", value: "#ef4444" },
  { name: "Orange", value: "#f97316" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Green", value: "#22c55e" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Indigo", value: "#6366f1" },
  { name: "Purple", value: "#a855f7" },
  { name: "Pink", value: "#ec4899" },
  { name: "Gray", value: "#6b7280" },
];

const TAG_ICONS = [
  "star",
  "celebration",
  "cake",
  "favorite",
  "local_fire_department",
  "diamond",
  "workspace_premium",
  "emoji_events",
  "restaurant",
  "deck",
  "wb_sunny",
  "nightlife",
  "family_restroom",
  "pets",
  "accessible",
  "eco",
];

export const TagManagement = defineComponent({
  name: "TagManagement",
  setup() {
    const trpc = useTrpc();

    const tags = ref<ReservationTag[]>([]);
    const loading = ref(true);
    const saving = ref(false);
    const error = ref("");

    // Add/edit form state
    const showForm = ref(false);
    const editingTag = ref<ReservationTag | null>(null);
    const formData = ref({
      name: "",
      color: TAG_COLORS[5].value,
      icon: "",
      description: "",
    });
    const formError = ref("");

    // Delete confirmation
    const deleteConfirmId = ref<string | null>(null);
    const deleting = ref(false);

    const isEditing = computed(() => !!editingTag.value);

    const loadTags = async () => {
      try {
        loading.value = true;
        error.value = "";
        const result = await trpc.reservationTags.list.query();
        tags.value = result;
      } catch (e) {
        error.value = e instanceof Error ? e.message : "Failed to load tags";
        console.error("Failed to load reservation tags:", e);
      } finally {
        loading.value = false;
      }
    };

    const resetForm = () => {
      formData.value = {
        name: "",
        color: TAG_COLORS[5].value,
        icon: "",
        description: "",
      };
      formError.value = "";
      editingTag.value = null;
      showForm.value = false;
    };

    const openAddForm = () => {
      resetForm();
      showForm.value = true;
    };

    const openEditForm = (tag: ReservationTag) => {
      editingTag.value = tag;
      formData.value = {
        name: tag.name,
        color: tag.color || TAG_COLORS[5].value,
        icon: tag.icon || "",
        description: tag.description || "",
      };
      formError.value = "";
      showForm.value = true;
    };

    const handleSave = async () => {
      if (!formData.value.name.trim()) {
        formError.value = "Tag name is required";
        return;
      }

      try {
        saving.value = true;
        formError.value = "";

        if (isEditing.value && editingTag.value) {
          await trpc.reservationTags.update.mutate({
            id: editingTag.value.id,
            name: formData.value.name.trim(),
            color: formData.value.color || null,
            icon: formData.value.icon || null,
            description: formData.value.description.trim() || null,
          });
        } else {
          await trpc.reservationTags.create.mutate({
            name: formData.value.name.trim(),
            color: formData.value.color || undefined,
            icon: formData.value.icon || undefined,
            description: formData.value.description.trim() || undefined,
            sort_order: tags.value.length,
          });
        }

        await loadTags();
        resetForm();
      } catch (e) {
        formError.value = e instanceof Error ? e.message : "Failed to save tag";
        console.error("Failed to save reservation tag:", e);
      } finally {
        saving.value = false;
      }
    };

    const handleDelete = async () => {
      if (!deleteConfirmId.value) return;

      try {
        deleting.value = true;
        await trpc.reservationTags.delete.mutate({
          id: deleteConfirmId.value,
        });
        await loadTags();
        deleteConfirmId.value = null;
      } catch (e) {
        console.error("Failed to delete reservation tag:", e);
      } finally {
        deleting.value = false;
      }
    };

    onMounted(() => {
      loadTags();
    });

    return () => (
      <div>
        <div class="flex items-center justify-between mb-4">
          <p class="text-sm text-base-content/40">
            Define custom experiences for guests to choose when booking (e.g.
            VIP, Birthday, Outdoor).
          </p>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            onClick={openAddForm}
          >
            <span class="material-symbols-outlined text-sm">add</span>
            Add Tag
          </button>
        </div>

        {/* Error */}
        {error.value && (
          <div class="bg-error/10 border border-error/20 rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
            <span class="material-symbols-outlined text-error text-lg">
              error
            </span>
            <span class="text-sm text-error">{error.value}</span>
            <button class="btn btn-ghost btn-xs ml-auto" onClick={loadTags}>
              Retry
            </button>
          </div>
        )}

        {/* Loading */}
        {loading.value ? (
          <div class="flex items-center justify-center py-12">
            <span class="loading loading-spinner loading-sm text-primary"></span>
          </div>
        ) : tags.value.length === 0 && !showForm.value ? (
          /* Empty state */
          <div class="flex-1 flex flex-col items-center justify-center py-12 animate-fade-up">
            <span class="material-symbols-outlined text-5xl text-base-content/20 mb-3">
              label
            </span>
            <div class="text-base-content/40 text-sm font-medium mb-1">
              No experiences defined yet
            </div>
            <div class="text-base-content/30 text-xs">
              Create custom experiences for your guests
            </div>
          </div>
        ) : (
          /* Tag list */
          <div class="space-y-2">
            {tags.value.map((tag, index) => (
              <div
                key={tag.id}
                class="flex items-center gap-3 px-4 py-3 bg-base-200 rounded-xl group hover:bg-base-200/60 transition-colors animate-fade-up"
                style={
                  index < 20 ? { animationDelay: `${index * 30}ms` } : undefined
                }
              >
                {/* Color dot + icon */}
                <div
                  class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: tag.color
                      ? `${tag.color}20`
                      : "oklch(var(--b3))",
                  }}
                >
                  {tag.icon ? (
                    <span
                      class="material-symbols-outlined text-base"
                      style={{ color: tag.color || undefined }}
                    >
                      {tag.icon}
                    </span>
                  ) : (
                    <span
                      class="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: tag.color || "oklch(var(--bc) / 0.3)",
                      }}
                    ></span>
                  )}
                </div>

                {/* Name + description */}
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-base-content truncate">
                    {tag.name}
                  </div>
                  {tag.description && (
                    <div class="text-xs text-base-content/40 truncate">
                      {tag.description}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs"
                    onClick={() => openEditForm(tag)}
                  >
                    <span class="material-symbols-outlined text-sm">edit</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs text-error hover:bg-error/10"
                    onClick={() => (deleteConfirmId.value = tag.id)}
                  >
                    <span class="material-symbols-outlined text-sm">
                      delete
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm.value && (
          <div class="mt-4 border border-primary/20 rounded-xl p-4 bg-base-200/30 space-y-4 animate-fade-up">
            <h4 class="text-sm font-semibold text-base-content">
              {isEditing.value ? "Edit Tag" : "New Tag"}
            </h4>

            {formError.value && (
              <div class="bg-error/10 border border-error/20 rounded-lg px-3 py-2 text-sm text-error">
                {formError.value}
              </div>
            )}

            {/* Name */}
            <div>
              <label class="block text-sm text-base-content/60 mb-1.5">
                Name *
              </label>
              <input
                type="text"
                class="input input-bordered w-full min-h-[44px] bg-base-200/60 border-base-content/[0.06] focus:border-primary/40 focus:bg-base-100 transition-colors"
                placeholder="e.g. VIP, Birthday, Outdoor"
                value={formData.value.name}
                onInput={(e) =>
                  (formData.value.name = (e.target as HTMLInputElement).value)
                }
                maxlength={50}
              />
            </div>

            {/* Color picker */}
            <div>
              <label class="block text-sm text-base-content/60 mb-1.5">
                Color
              </label>
              <div class="flex flex-wrap gap-2">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    class={`w-8 h-8 rounded-lg border-2 transition-all ${
                      formData.value.color === color.value
                        ? "border-base-content scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => (formData.value.color = color.value)}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Icon picker */}
            <div>
              <label class="block text-sm text-base-content/60 mb-1.5">
                Icon
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${
                    !formData.value.icon
                      ? "border-primary bg-primary/10"
                      : "border-base-content/10 hover:border-base-content/30"
                  }`}
                  onClick={() => (formData.value.icon = "")}
                  title="No icon"
                >
                  <span class="material-symbols-outlined text-sm text-base-content/40">
                    block
                  </span>
                </button>
                {TAG_ICONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    class={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${
                      formData.value.icon === icon
                        ? "border-primary bg-primary/10"
                        : "border-base-content/10 hover:border-base-content/30"
                    }`}
                    onClick={() => (formData.value.icon = icon)}
                    title={icon}
                  >
                    <span
                      class="material-symbols-outlined text-base"
                      style={{
                        color:
                          formData.value.icon === icon
                            ? formData.value.color || undefined
                            : undefined,
                      }}
                    >
                      {icon}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label class="block text-sm text-base-content/60 mb-1.5">
                Description
              </label>
              <input
                type="text"
                class="input input-bordered w-full min-h-[44px] bg-base-200/60 border-base-content/[0.06] focus:border-primary/40 focus:bg-base-100 transition-colors"
                placeholder="Optional description for staff"
                value={formData.value.description}
                onInput={(e) =>
                  (formData.value.description = (
                    e.target as HTMLInputElement
                  ).value)
                }
                maxlength={200}
              />
            </div>

            {/* Preview */}
            <div>
              <label class="block text-sm text-base-content/60 mb-1.5">
                Preview
              </label>
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: formData.value.color
                      ? `${formData.value.color}20`
                      : "oklch(var(--b3))",
                    color: formData.value.color || undefined,
                  }}
                >
                  {formData.value.icon && (
                    <span class="material-symbols-outlined text-sm">
                      {formData.value.icon}
                    </span>
                  )}
                  {formData.value.name || "Tag Name"}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div class="flex justify-end gap-2 pt-2 border-t border-base-content/[0.06]">
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={resetForm}
                disabled={saving.value}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={saving.value || !formData.value.name.trim()}
              >
                {saving.value ? (
                  <span class="loading loading-spinner loading-sm"></span>
                ) : (
                  <span class="material-symbols-outlined text-sm">
                    {isEditing.value ? "save" : "add"}
                  </span>
                )}
                {saving.value
                  ? "Saving..."
                  : isEditing.value
                    ? "Save Changes"
                    : "Add Tag"}
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId.value && (
          <div class="modal modal-open">
            <div class="modal-box">
              <h3 class="text-lg font-semibold text-base-content mb-4">
                Delete Tag
              </h3>
              <p class="text-sm text-base-content/60 mb-6">
                Are you sure you want to delete this tag? This action cannot be
                undone.
              </p>
              <div class="flex gap-3 justify-end">
                <button
                  class="btn btn-ghost btn-sm"
                  onClick={() => (deleteConfirmId.value = null)}
                  disabled={deleting.value}
                >
                  Cancel
                </button>
                <button
                  class="btn btn-error btn-sm"
                  onClick={handleDelete}
                  disabled={deleting.value}
                >
                  {deleting.value ? (
                    <span class="loading loading-spinner loading-xs"></span>
                  ) : (
                    <span class="material-symbols-outlined text-sm">
                      delete
                    </span>
                  )}
                  Delete
                </button>
              </div>
            </div>
            <div
              class="modal-backdrop"
              onClick={() => (deleteConfirmId.value = null)}
            />
          </div>
        )}
      </div>
    );
  },
});
